import React, { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { initializeAuth } from "./store/slices/authSlice";

// Layout Components
import AdminLayout from "./layout/AdminLayout";
import LoadingScreen from "./components/LoadingScreen/LoadingScreen";

// Auth Components
import Login from "./pages/Auth/Login";
import EmailVerification from "./pages/Auth/EmailVerification";

// Main Pages
import Dashboard from "./pages/Dashboard/Dashboard";
import Users from "./pages/Users/Users";
import UserDetail from "./pages/Users/UserDetail";
import CreateUser from "./pages/Users/CreateUser";
import Courses from "./pages/Courses/Courses";
import CourseDetail from "./pages/Courses/CourseDetail";
import CreateCourse from "./pages/Courses/CreateCourse";
import Batches from "./pages/Batches/Batches";
import BatchDetail from "./pages/Batches/BatchDetail";
import CreateBatch from "./pages/Batches/CreateBatch";
import Modules from "./pages/Modules/Modules";
import ModuleDetail from "./pages/Modules/ModuleDetail";
import CreateModule from "./pages/Modules/CreateModule";
import Lectures from "./pages/Lectures/Lectures";
import LectureDetail from "./pages/Lectures/LectureDetail";
import CreateLecture from "./pages/Lectures/CreateLecture";
import Enrollments from "./pages/Enrollments/Enrollments";
import EnrollmentDetail from "./pages/Enrollments/EnrollmentDetail";
import CreateEnrollment from "./pages/Enrollments/CreateEnrollment";
import Payments from "./pages/Payments/Payments";
import PaymentDetail from "./pages/Payments/PaymentDetail";
import Enquiries from "./pages/Enquiries/Enquiries";
import EnquiryDetail from "./pages/Enquiries/EnquiryDetail";
import Queries from "./pages/Queries/QueriesList";
import QueryDetail from "./pages/Queries/QueryDetail";
import Settings from "./pages/Settings/Settings";
import InviteStaff from "./pages/InviteStaff/InviteStaff";
import HiringRequests from "./pages/HiringRequests/HiringRequests";
import HiringRequestDetail from "./pages/HiringRequests/HiringRequestDetail";
import StudentProfiles from "./pages/StudentProfiles/StudentProfiles";
import RecruiterJobs from "./pages/RecruiterJobs/RecruiterJobs";

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole = null }) => {
  const { isAuthenticated, isInitialized, user } = useSelector(
    (state) => state.auth
  );

  if (!isInitialized) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Role-based access control
  if (requiredRole && user?.role !== requiredRole) {
    return <Navigate to="/" replace />;
  }

  // Only allow admin/superadmin roles
  if (user && !["admin", "superadmin"].includes(user.role)) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  const dispatch = useDispatch();
  const { isInitialized, isLoading } = useSelector((state) => state.auth);

  useEffect(() => {
    dispatch(initializeAuth());
  }, [dispatch]);

  if (!isInitialized || isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      {/* Auth Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/verify-email" element={<EmailVerification />} />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard */}
        <Route index element={<Dashboard />} />

        {/* Admin Modules */}
        <Route path="users" element={<Users />} />
        <Route path="users/new" element={<CreateUser />} />
        <Route path="users/:userId" element={<UserDetail />} />
        <Route path="courses" element={<Courses />} />
        <Route path="courses/new" element={<CreateCourse />} />
        <Route path="courses/:courseId" element={<CourseDetail />} />
        <Route path="batches" element={<Batches />} />
        <Route path="batches/new" element={<CreateBatch />} />
        <Route path="batches/:batchId" element={<BatchDetail />} />
        <Route path="modules" element={<Modules />} />
        <Route path="modules/new" element={<CreateModule />} />
        <Route path="modules/:moduleId" element={<ModuleDetail />} />
        <Route path="lectures" element={<Lectures />} />
        <Route path="lectures/new" element={<CreateLecture />} />
        <Route path="lectures/:lectureId" element={<LectureDetail />} />
        <Route path="enrollments" element={<Enrollments />} />
        <Route path="enrollments/new" element={<CreateEnrollment />} />
        <Route
          path="enrollments/:enrollmentId"
          element={<EnrollmentDetail />}
        />
        <Route path="payments" element={<Payments />} />
        <Route path="payments/:paymentId" element={<PaymentDetail />} />
        <Route path="enquiries" element={<Enquiries />} />
        <Route path="enquiries/:enquiryId" element={<EnquiryDetail />} />
        <Route path="hiring-requests" element={<HiringRequests />} />
        <Route path="hiring-requests/:id" element={<HiringRequestDetail />} />
        <Route path="student-profiles" element={<StudentProfiles />} />
        <Route path="recruiter-jobs" element={<RecruiterJobs />} />
        <Route path="queries" element={<Queries />} />
        <Route path="queries/:queryId" element={<QueryDetail />} />
        <Route path="settings" element={<Settings />} />
        <Route path="settings/invites" element={<InviteStaff />} />
      </Route>

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
